/* eslint-disable @angular-eslint/directive-class-suffix */
import {ChangeDetectorRef, Directive, Input, OnChanges, OnInit} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, ValidatorFn} from '@angular/forms';
import {
  ClauseFragment,
  Fragment,
  FragmentService,
  LockService,
  ManagedSubscriber,
  User,
  UserService,
} from '@nh/angular-tses';
import {DepartureInformationFragment} from 'app/fragment/types/departure-information-fragment';
import {Observable, of} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {ErrorMessages} from '../../../../core/validation/error-messages';
import {DepartureInformationType} from '../departure-information-type';

/**
 * Base form component for all departure information types.
 */
@Directive()
export abstract class BaseFormComponent extends ManagedSubscriber implements OnInit, OnChanges {
  @Input() public formGroup: UntypedFormGroup;
  @Input() public clause: ClauseFragment;

  protected _disabled: boolean;
  protected _canLock: boolean;
  protected _lockingUser: Observable<User>;

  public id: DepartureInformationType;
  public displayName: string = '';
  public required: boolean = false;
  public validators: ValidatorFn[] = [];
  public hintMessage?: string = '';

  constructor(
    protected _fragmentService: FragmentService,
    protected _lockService: LockService,
    protected _userService: UserService,
    protected _cdr: ChangeDetectorRef
  ) {
    super();
  }

  /**
   * Initialises this component, setting up subscrpitions and adds the form control to the group.
   */
  public ngOnInit(): void {
    this._setup();
  }

  public ngOnChanges(): void {
    this._setup();
  }

  /**
   * Setup form control and initialise subscriptions.
   */
  protected _setup(): void {
    this._initFormGroup();
    this._initSubscriptions();
    this._updateLock();
  }

  /**
   * Adds the form control to the group. We can override this method if we want to initialise other parts
   * of the component after the form group is initialised.
   */
  protected _initFormGroup(): void {
    this.formGroup.addControl(this.id, new UntypedFormControl(this.depInfoFragment.value, this.validators));
  }

  protected _onUpdatePredicate(fragment: Fragment): boolean {
    return fragment.id.equals(this.clause.firstChild.id);
  }

  /**
   * Setup required subscriptions for this components departure information fragment.
   */
  protected _initSubscriptions(): void {
    this.ngOnDestroy();
    this.subscriptions.push(
      this._fragmentService.onUpdate(
        (f: DepartureInformationFragment) => {
          this._patchValue(f);
        },
        (f: Fragment) => this._onUpdatePredicate(f)
      ),
      this._lockService.onLockChange(this.clause, this._onLockUpdate.bind(this)),
      this.formGroup.valueChanges
        .pipe(filter(this._hasChangedLocally.bind(this)))
        .subscribe(this._updateFragment.bind(this))
    );
  }

  /**
   * This is used to check if the form has changed locally so that we should then update the fragment. This may be
   * overridden if detecting a local change is more complicated than checking if the current fragment is different
   * to the form.
   */
  protected _hasChangedLocally(): boolean {
    return String(this.depInfoFragment.value) !== String(this.formGroup.get(this.id).value);
  }

  /**
   * This is used to check if the form has changed locally so that we should then update the fragment. This may be
   * overridden if detecting a local change is more complicated than checking if the current fragment is different
   * to the form.
   */
  protected _isInputValid(): boolean {
    return this.formGroup.get(this.id).valid;
  }

  /**
   * Updates the fragment value. Returns a promise to allow overriding classes to wait for resolution of request.
   * This gets called only when the form is updated locally, after the form group is updated.
   *
   * @returns {Promise<number>}   Promise resolving to the HTTP status code of the request
   */
  protected _updateFragment(): Promise<number> {
    this.depInfoFragment.value = this.formGroup.get(this.id).value;
    return this._fragmentService.update(this.depInfoFragment).then((n: number) => {
      this._afterUpdateHook();
      return n;
    });
  }

  /**
   * This method can be overridden to hook into fragment updates. This gets called after an update, sent locally to
   * the fragment service, has resolved.
   */
  protected _afterUpdateHook(): void {}

  /**
   * Sets the canLock value and udpates the locking user.
   *
   * @param canLock {boolean}   True if the user can lock the clause
   */
  protected _onLockUpdate(canLock: boolean): void {
    if (canLock !== this._canLock) {
      this.canLock = canLock;
      this._lockingUser = canLock
        ? null
        : this._userService.getUserFromId(this._lockService.getLock(this.clause).userId || null);
      this._cdr.markForCheck();
    }
  }

  /**
   * Public setter for the _disabled variable. Updates the form on setting.
   *
   * @param disabled {boolean}   True if form should be disabled
   */
  @Input()
  public set disabled(disabled: boolean) {
    this._disabled = disabled;
    this._updateLock();
  }

  /**
   * Sets the canLock value, determining whether the current user can lock the clause.
   *
   * @param canLock {boolean}   Can lock value
   */
  public set canLock(canLock: boolean) {
    this._canLock = canLock;
    this._updateLock();
  }

  /**
   * Updates the lock for the form group based on the disabled and canLock values.
   */
  protected _updateLock(): void {
    if (!!this.formGroup) {
      !this._canLock || this._disabled ? this.formGroup.disable() : this.formGroup.enable();
      this._cdr.markForCheck();
    }
  }

  /**
   * Public getter for the canLock value.
   *
   * @returns {boolean}   True if user can lock the clause
   */
  public get canLock(): boolean {
    return this._canLock;
  }

  /**
   * Public getter for the departure information fragment.
   *
   * @returns {DepartureInformationFragment}   The departure information fragment
   */
  public get depInfoFragment(): DepartureInformationFragment {
    return this.clause && (this.clause.firstChild as DepartureInformationFragment);
  }

  /**
   * Public getter for the value of the departure information fragment.
   *
   * @returns {string}   The value of the departure information fragment
   */
  public get value(): string {
    return this.depInfoFragment.value;
  }

  /**
   * Public getter for the locked text.
   *
   * @returns {Observable<string>}   Observable object resolving to the locked text
   */
  public get lockedText(): Observable<string> {
    const locked: string = `Locked`;
    return this._lockingUser
      ? this._lockingUser.pipe(map((user: User) => `${locked} by ${user.firstName} ${user.lastName}`))
      : of(locked);
  }

  /**
   * Marks the control for the form group as touched. This can be used to manually mark a control as touched that otherwise
   * would not be, for example control that doesn't use and angular component as its display.
   *
   * @param onlySelf {boolean}   True if only this control should be marked; defaults to false
   */
  public markAsTouched(onlySelf: boolean = false): void {
    this.formGroup.get(this.id).markAsTouched({onlySelf});
    this._cdr.markForCheck();
  }

  /**
   * Patches the value from the departure information fragment onto the form control. This is called when a departure
   * information fragment changes, whether that change originated locally or was received via websocket.
   *
   * @param fragment  {DepartureInformationFragment}   The updated fragment
   * @param emitEvent {boolean}                        Whether the form should broadcast this new value; defaults to false
   */
  protected _patchValue(fragment: DepartureInformationFragment, emitEvent: boolean = false): void {
    this.formGroup.patchValue({[this.id]: fragment.value}, {emitEvent});
    this._cdr.markForCheck();
  }

  /**
   * Returns the first user friendly error message to be displayed in the mat-error components.
   *
   * @returns {string}   The first error message
   */
  public get errorMessage(): string {
    return ErrorMessages.fromControl(this.formGroup.get(this.id))[0];
  }

  /**
   * Locks the clause fragment for this component.
   */
  public lock(): void {
    this._lockService.lock(this.clause);
  }

  /**
   * Un-locks the clause fragment for this component.
   */
  public unlock(): void {
    this._lockService.unlock(this.clause);
  }
}
